import { useEffect, useLayoutEffect, useState } from 'react';

/**
 * Placement and dismissal for a popover portaled to <body> (menus, the date and
 * time pickers). A panel drawn inside its field is cut off by a card's
 * `overflow:hidden` or a modal's scrolling body, so it is `position: fixed`
 * instead: under the field when it fits, above it when there is more room
 * there (a field low in a phone's bottom sheet), always inside the viewport,
 * following the field when the page or a dialog scrolls and when the panel
 * itself changes size (a month with six weeks).
 *
 * A pointer down outside, or Escape, closes it. Escape is taken in the capture
 * phase on `window`, so it never also reaches the dialog the panel sits in.
 *
 * Port of darsly-front-platform `use-anchored-popover.ts` (spec §3.10).
 *
 * @param {boolean} open
 * @param {(open: boolean) => void} setOpen
 * @param {{ current: HTMLElement | null }} anchorRef  the field the panel hangs from
 * @param {{ current: HTMLElement | null }} popRef     the panel itself
 * @param {{ matchWidth?: boolean, align?: 'start' | 'end' }} [options]  matchWidth: the panel is at least as
 *   wide as the field; align 'end': the panel's right edge meets the field's right edge (a "⋯" button)
 * @returns {{ top: number, left: number, above: boolean, zIndex: number, minWidth?: number } | null}
 *   null until the panel has been measured; render it hidden until then.
 */

const GAP = 8;
// The popover layer of the stacking scale (--z-popover in index.css)
const BASE_Z = 60;

/**
 * Where to portal a panel anchored to `anchor`: <body>, except inside a modal dialog
 * (`role="dialog" aria-modal="true"`), whose panel then lives in the dialog itself — a
 * listbox outside an aria-modal dialog is hidden from screen readers, and the dialog's
 * focus trap keeps working. A `position: fixed` panel is never clipped by the dialog's
 * `overflow`, so nothing is lost.
 */
export function popoverRoot(anchor) {
  if (typeof document === 'undefined') return null;
  const dialog = anchor && typeof anchor.closest === 'function' ? anchor.closest('[role="dialog"][aria-modal="true"]') : null;
  return dialog || document.body;
}

/**
 * The z-index a panel anchored to `anchor` needs. Normally the popover layer; but a
 * legacy overlay that still carries a literal z-index (1000 …) would paint over it, so
 * the panel goes one above the highest z-index among the anchor's positioned ancestors.
 */
export function popoverZIndex(anchor) {
  let z = BASE_Z;
  if (!anchor || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return z;
  for (let el = anchor; el && el !== document.body && el.nodeType === 1; el = el.parentElement) {
    const style = window.getComputedStyle(el);
    if (style.position === 'static') continue;
    const value = parseInt(style.zIndex, 10);
    if (Number.isFinite(value) && value >= z) z = value + 1;
  }
  return z;
}

export function useAnchoredPopover(open, setOpen, anchorRef, popRef, options = {}) {
  const { matchWidth = false, align = 'start' } = options;
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    // Resolved once per opening: walking the ancestors on every scroll tick would be wasteful.
    const zIndex = popoverZIndex(anchorRef.current);
    const place = () => {
      const anchor = anchorRef.current;
      const pop = popRef.current;
      if (!anchor || !pop) return;
      const field = anchor.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = pop.offsetWidth;
      const h = pop.offsetHeight;
      const roomBelow = vh - field.bottom - GAP;
      const roomAbove = field.top - GAP;
      const above = roomBelow < h + GAP && roomAbove > roomBelow;
      const top = above ? field.top - GAP - h : field.bottom + GAP;
      const next = {
        top: Math.round(Math.max(GAP, Math.min(top, vh - h - GAP))),
        left: Math.round(Math.max(GAP, Math.min(align === 'end' ? field.right - w : field.left, vw - w - GAP))),
        above,
        zIndex,
        minWidth: matchWidth ? Math.round(field.width) : undefined,
      };
      setPos((prev) => (
        prev
        && prev.top === next.top
        && prev.left === next.left
        && prev.above === next.above
        && prev.zIndex === next.zIndex
        && prev.minWidth === next.minWidth
          ? prev
          : next
      ));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    if (observer && popRef.current) observer.observe(popRef.current);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      if (observer) observer.disconnect();
    };
  }, [open, anchorRef, popRef, matchWidth, align]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      const target = e.target;
      if (anchorRef.current?.contains(target) || popRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      // Capture phase on window: the dialog under the panel never sees this Escape.
      e.stopPropagation();
      setOpen(false);
      const trigger = anchorRef.current?.querySelector?.('button, input, [tabindex]');
      if (trigger && typeof trigger.focus === 'function') trigger.focus({ preventScroll: true });
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, setOpen, anchorRef, popRef]);

  return pos;
}

export default useAnchoredPopover;
